import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * The "Recently viewed" row.
 *
 * The page renders this section as an empty, hidden shell. Once it is near
 * the viewport, the ids Horizon keeps in localStorage are sent to the search
 * route as "id:1 OR id:2 ..." with this section's id, and the Section
 * Rendering API returns the section drawn with those products -- the same
 * request Horizon's predictive search makes for its own recently-viewed list.
 *
 * Nothing is fetched when there is no history, so a first-time visitor costs
 * no request at all.
 */
class RecentlyViewedProducts extends Component {
  #observer = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#load();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.performed === 'true') return;
    // Watched through the section wrapper, not this element: this element is
    // `hidden` until it has products, and a display:none element never
    // intersects, so observing it would never load anything.
    this.#observer.observe(this.closest('.shopify-section') || this.parentElement || this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer.disconnect();
  }

  async #load() {
    const { sectionId, searchUrl, currentProductId, limit } = this.dataset;
    if (!sectionId || !searchUrl) return;

    // The product on screen is not "recently viewed" on its own page.
    const ids = RecentlyViewed.getProducts()
      .filter((/** @type {string} */ id) => id && id !== currentProductId)
      .slice(0, Number(limit) || 12);

    if (ids.length === 0) return;

    const url = new URL(searchUrl, location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(url);
      if (!response.ok) return;

      const html = await response.text();
      // An empty render means none of the ids still resolve to a product
      // (deleted, unpublished). Leave the section hidden rather than show a
      // heading over nothing.
      if (!html.includes('resource-list__item')) return;

      morphSection(sectionId, html, { injectStylesheet: true });
    } catch (error) {
      // History is a convenience. A failure here must never surface on a
      // product page, so the section simply stays hidden.
      console.warn('[recently-viewed] could not load:', error);
    }
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
